// ============================================================
// TERROR — THE TRADE POST ENGINE (player ↔ player)
// ------------------------------------------------------------
// Two lanes, no UI in here:
//
//   SELL  — dupes → ◕. NOT a new price ladder: this file surfaces
//           progression's scrapCopies() / SCRAP_COIN untouched (T21,
//           THE SCRAP LANE). The only thing added is a deck-usage
//           read so the UI can WARN (never block) before a sale
//           drops a card below what a saved set uses.
//
//   TRADE — two self-applied halves. There are no Cloud Functions
//           and profiles/$uid is owner-only write (rules.json:
//           `".write": "auth != null && auth.uid === $uid"`), so no
//           client can ever move a card INTO another player's
//           collection. What a client CAN do is remove from its own
//           collection and add to its own collection. A trade is
//           therefore two independent, symmetric, self-applied
//           halves coordinated through a shared node that anybody
//           authed may write (rules.json: `games/terror` is
//           `".read": "auth != null", ".write": "auth != null"`).
//
// THE SAFETY ARGUMENT IS THE ORDER OF WRITES. A side never credits
// itself until it has (1) sealed exactly what it will give and
// receive under its OWN profile, (2) debited its own give, and
// (3) SEEN the other side's debit. The sealed copy is the receipt:
// it is written before anything moves, it is only ever read by its
// owner, and its three stamps (debitedAt / creditedAt / reclaimedAt)
// make every step idempotent — a refresh mid-settlement re-runs the
// same function and it skips what already happened.
//
// THE SHARED NODE IS COORDINATION, NOT TRUTH. Both players can write
// it (the rules permit that on purpose — it's the drawer). A hostile
// counterparty can corrupt it, and the worst they can do is stall
// the trade: my credit is computed from MY sealed copy, never from
// the shared node, and my credit does not fire until their debit is
// visible — either on the shared node or, because profiles are
// world-READABLE (`".read": "auth != null"`), on THEIR sealed copy.
// A stalled trade expires; after expiry a debited side that never
// credited reclaims its own give (reclaimTrade). Nothing in this
// file can create a card from nothing except addCardToCollection on
// the crediting side, which is gated behind the other side's debit.
//
// Shapes (Firebase drops empty objects — always normalize on read):
//   games/terror/trades/{id}
//     { a:{uid,name,offer:{cards:{cardId:{plain:n,tiers:{tier:n}}},coins:n},
//          locked:ts|null, confirmed:ts|null, debited:ts|null, credited:ts|null},
//       b:{same}, status, rev, createdAt: serverTimestamp(), expiresAt, cancelledBy }
//   profiles/{uid}/trades/{id}
//     { counterparty, give:{cards,coins}, receive:{cards,coins}, rev,
//       confirmedAt, expiresAt, debitedAt|null, creditedAt|null, reclaimedAt|null }
//
// State machine:
//   draft → (both locked, same rev) locked → (both confirmed, same rev)
//   confirmed → (both debited) debited → (both credited) settled
//   Any offer edit bumps rev and DELETES both locked + confirmed.
//   cancelled: allowed until either side has debited.
//   expired: 10 min in draft/locked, 24 h after confirmed.
// ============================================================

import { useState, useEffect } from "react";
import {
  ref, get, set, update, push, onValue, runTransaction, serverTimestamp,
} from "firebase/database";
import { db, auth, GAME_BASE } from "./firebase";
import {
  addCardToCollection, awardCoins, spendCoins, scrapCopies,
  scrapValueOf, readSets,
} from "./progression";
import { clipName, serverNow, IE_GAME_URLS } from "./ieSocial";
import {
  ESCENDANTS, TOMES, IMPERA, UHLTIS, AFFINITIES, PROPHECIES, CURIOS, ESCLINGS,
} from "./cards";

// ── DIALS ────────────────────────────────────────────────────
export const TRADE_COIN_CAP     = 100000;              // ⟵ DIAL  ◕ per side, per trade.
                                                       //   Must stay ≤ 200000: rules.json clamps a single
                                                       //   chocolateCoins raise to `newData.val() - data.val() <= 200000`
                                                       //   and awardCoins is ONE transaction.
export const DRAFT_TTL_MS       = 10 * 60 * 1000;      // ⟵ DIAL  draft / locked go cold
export const CONFIRMED_TTL_MS   = 24 * 60 * 60 * 1000; // ⟵ DIAL  a confirmed-but-unsettled half can be reclaimed after this
export const TRADE_SOURCE       = "trade";             // provenance id stamped on every credited copy (SourceSigil row needed)
export const TRADE_STATUS = ["draft", "locked", "confirmed", "debited", "settled", "cancelled", "expired"];

// ── PATHS — one base constant, never scattered strings (IE_FIREBASE_LAW §2)
export const tradesPath = ()          => `${GAME_BASE}/trades`;
export const tradePath  = (id)        => `${GAME_BASE}/trades/${id}`;
export const sealedPath = (uid, id)   => `profiles/${uid}/trades/${id}`;
export const sealedRoot = (uid)       => `profiles/${uid}/trades`;

// ── THE UID GATE — progression's _uidKey is not exported, so the same
// filter lives here: a profile path is only safe keyed by an Auth uid,
// and the caller's uid must BE the signed-in uid. Never a name.
const _looksLikeUid = (k) => typeof k === "string" && /^[A-Za-z0-9]{20,40}$/.test(k);
const _me = () => auth.currentUser?.uid || null;
const _own = (uid, fn) => {
  if (!_looksLikeUid(uid)) { console.error(`[trading] ${fn}: non-uid key refused`, uid); return null; }
  const me = _me();
  if (me && me !== uid) { console.error(`[trading] ${fn}: uid is not the signed-in player — refused`); return null; }
  return uid;
};

// ── CARD INDEX — cards.js has no getCard(); build one lookup once.
const CARD_INDEX = (() => {
  const m = {};
  for (const list of [ESCENDANTS, TOMES, IMPERA, UHLTIS, AFFINITIES, PROPHECIES, CURIOS, ESCLINGS]) {
    for (const c of list || []) if (c && c.id) m[c.id] = c;
  }
  return m;
})();
export const cardOf = (id) => CARD_INDEX[id] || null;
export const cardName = (id) => CARD_INDEX[id]?.name || id;

// Display labels for the stored foil keys (legacy keys included so a
// retired tier still reads as a word, not a slug).
export const TIER_LABELS = {
  holo_silver: "Silver", holo_gold: "Gold", holo_khao: "Khao", holo_jynxi: "J'ynxi",
  holo_depths: "Depths", holo_solrend: "Solrend", holo_aeternus: "A'eterni",
  holo_mythic: "Mythic", holo_void: "Void", holo_shadow: "Shadow",
};
export const tierLabel = (t) => TIER_LABELS[t] || String(t || "").replace(/^holo_/, "");

// ════════════════════════════════════════════════════════════
// OFFERS
// ════════════════════════════════════════════════════════════
export const emptyOffer = () => ({ cards: {}, coins: 0 });

// Firebase strips empty maps and a hostile peer can write anything, so
// every offer read off the wire passes through here before it is
// trusted for arithmetic. Output is always the full shape, ints ≥ 0.
export const normalizeOffer = (o) => {
  const out = emptyOffer();
  if (!o || typeof o !== "object") return out;
  out.coins = Math.max(0, Math.floor(Number(o.coins) || 0));
  for (const [cardId, v] of Object.entries(o.cards || {})) {
    if (!cardId || !v || typeof v !== "object") continue;
    const plain = Math.max(0, Math.floor(Number(v.plain) || 0));
    const tiers = {};
    for (const [t, n] of Object.entries(v.tiers || {})) {
      const k = Math.max(0, Math.floor(Number(n) || 0));
      if (k > 0) tiers[t] = k;
    }
    if (plain > 0 || Object.keys(tiers).length) out.cards[cardId] = { plain, tiers };
  }
  return out;
};

export const offerIsEmpty = (o) => {
  const n = normalizeOffer(o);
  return n.coins === 0 && Object.keys(n.cards).length === 0;
};

// Flatten an offer into the [{cardId, tier, copies}] list both
// decrementCopies and scrapCopies speak.
export const offerToItems = (o) => {
  const n = normalizeOffer(o);
  const items = [];
  for (const [cardId, v] of Object.entries(n.cards)) {
    if (v.plain > 0) items.push({ cardId, tier: null, copies: v.plain });
    for (const [t, k] of Object.entries(v.tiers)) items.push({ cardId, tier: t, copies: k });
  }
  return items;
};

// ── WHAT A COLLECTION CAN PUT ON THE TABLE ───────────────────
// Per card: { plain, tiers:{tier: n} } of TRADEABLE copies.
//   • plain = `count` (count encodes plain copies; a card owned only as
//     a foil still carries count ≥ 1 from addCardToCollection — that
//     one is tradeable as a plain, which is the same reading scrapCopies
//     makes of `count`).
//   • foils: THE MYTHICAL LAW — a Vault single is ONE PER ACCOUNT, so a
//     foil copy whose provenance is the Vault can never change hands.
//     sourceTiers[tier].vault copies are subtracted; when a tier has no
//     per-tier provenance at all and the card's ONLY flat source is
//     "vault", every foil of it is treated as Vault-born. Copies with no
//     provenance at all are tradeable (legacy grants predate sigils).
export const tradeableOf = (collection) => {
  const out = {};
  for (const [cardId, rec] of Object.entries(collection || {})) {
    if (!rec || typeof rec !== "object") continue;
    const plain = Math.max(0, Math.floor(Number(rec.count) || 0));
    const tiers = {};
    const flatSources = Object.keys(rec.sources || {}).filter((s) => (rec.sources[s] || 0) > 0);
    const vaultOnly = flatSources.length > 0 && flatSources.every((s) => s === "vault");
    for (const [t, n] of Object.entries(rec.holoTiers || {})) {
      const owned = Math.max(0, Math.floor(Number(n) || 0));
      if (owned <= 0) continue;
      const st = rec.sourceTiers?.[t];
      let vaultCopies = 0;
      if (st && typeof st === "object") vaultCopies = Math.max(0, Math.floor(Number(st.vault) || 0));
      else if (vaultOnly) vaultCopies = owned;
      const free = owned - vaultCopies;
      if (free > 0) tiers[t] = free;
    }
    if (plain > 0 || Object.keys(tiers).length) out[cardId] = { plain, tiers };
  }
  return out;
};

// Pure. Returns { ok, errors:[string] }. `coins` = the player's ◕
// balance; omit to skip the balance check (the engine re-checks at
// debit time via spendCoins anyway — that is the write that matters).
export const validateOffer = (collection, offer, { coins = null } = {}) => {
  const errors = [];
  const o = normalizeOffer(offer);
  const can = tradeableOf(collection);
  // No Divine Crests, ever — an offer only has a `coins` field and it is ◕.
  if (offer && typeof offer === "object" && ("crests" in offer || "divineCrests" in offer)) {
    errors.push("Divine Crests cannot be traded.");
  }
  if (o.coins > TRADE_COIN_CAP) errors.push(`Coins are capped at ${TRADE_COIN_CAP.toLocaleString()} ◕ per trade.`);
  if (coins !== null && o.coins > (Number(coins) || 0)) errors.push("Not enough Chocolate Coins for that offer.");
  for (const [cardId, v] of Object.entries(o.cards)) {
    if (!cardOf(cardId)) { errors.push(`Unknown card: ${cardId}`); continue; }
    const have = can[cardId] || { plain: 0, tiers: {} };
    if (v.plain > have.plain) errors.push(`${cardName(cardId)}: only ${have.plain} plain cop${have.plain === 1 ? "y" : "ies"} to trade.`);
    for (const [t, k] of Object.entries(v.tiers)) {
      const h = have.tiers[t] || 0;
      if (k > h) errors.push(`${cardName(cardId)} (${tierLabel(t)}): only ${h} tradeable — Vault singles stay home.`);
    }
  }
  return { ok: errors.length === 0, errors };
};

// ════════════════════════════════════════════════════════════
// DECK USAGE — for the SELL warning (warn, never block)
// ════════════════════════════════════════════════════════════
// The max copies of each card any saved set (or the live loadout
// mirror) uses. readSets() already folds the legacy loadout into Set 1.
export const deckUsageOf = (profile) => {
  const use = {};
  const bump = (map) => {
    for (const [id, qty] of Object.entries(map || {})) {
      const n = Number(qty) || 0;
      if (n > (use[id] || 0)) use[id] = n;
    }
  };
  try {
    const { sets } = readSets(profile);
    for (const s of sets) { bump(s.grimoire); bump(s.luxmoire); }
  } catch (_) { /* a malformed profile just yields no warnings */ }
  bump(profile?.loadout?.grimoire);
  bump(profile?.loadout?.luxmoire);
  return use;
};

// [{cardId, used, after}] for every plain sale that would leave fewer
// copies than a saved set needs. Foil sales never touch `count`.
export const deckWarningsFor = (profile, items) => {
  const use = deckUsageOf(profile);
  const coll = profile?.collection || {};
  const drop = {};
  for (const it of items || []) if (it && !it.tier) drop[it.cardId] = (drop[it.cardId] || 0) + (it.copies || 0);
  const out = [];
  for (const [cardId, n] of Object.entries(drop)) {
    const after = Math.max(0, (coll[cardId]?.count || 0) - n);
    if ((use[cardId] || 0) > after) out.push({ cardId, used: use[cardId], after });
  }
  return out;
};

// ════════════════════════════════════════════════════════════
// SELL — the scrap lane, surfaced
// ════════════════════════════════════════════════════════════
export const sellValueOf = (items) =>
  (items || []).reduce((sum, it) => sum + (it?.copies || 0) * scrapValueOf(it?.tier || null), 0);

// Straight through to progression. The floor of one is enforced INSIDE
// scrapCopies at the write, so a stale UI can never melt a last copy.
export const sellCopies = async (uid, items) => {
  uid = _own(uid, "sellCopies");
  if (!uid) return { success: false, reason: "bad-uid", paid: 0, results: [] };
  return scrapCopies(uid, items);
};

// ════════════════════════════════════════════════════════════
// decrementCopies — the trade's own subtraction primitive
// ════════════════════════════════════════════════════════════
// Modelled on scrapCopies' transaction body (progression.js ~1662),
// with the floor of one OFF by default: a trade may give away the
// last copy — the counterparty asked for it, and it is coming back to
// somebody. ALL-OR-NOTHING PER CARD: if the node cannot cover the whole
// request for that card the transaction aborts and the card is
// reported as `short`, so a concurrent scrap in another tab can never
// leave a half-debited give. The node is NEVER deleted and `count`
// stays present (rules: `".validate": "newData.hasChildren(['count'])"`,
// every numeric leaf validates `>= 0` — verified against rules.json).
// Write site rule: profiles/$uid `".write": "auth != null && auth.uid === $uid"`.
export const decrementCopies = async (uid, items, { floorOfOne = false } = {}) => {
  uid = _own(uid, "decrementCopies");
  if (!uid || !Array.isArray(items) || !items.length) return { ok: false, removed: [], short: [] };
  const byCard = new Map();
  for (const it of items) {
    if (!it || !it.cardId) continue;
    const list = byCard.get(it.cardId) || [];
    list.push({ tier: it.tier || null, copies: Math.max(1, Math.floor(it.copies || 1)) });
    byCard.set(it.cardId, list);
  }
  const removed = [];
  const short = [];
  for (const [cardId, reqs] of byCard) {
    const cardRef = ref(db, `profiles/${uid}/collection/${cardId}`);
    // CACHE SEED — same dance as scrapCopies: the updater sees the local
    // cache (null when never listened to) first; a get() primes a seed so
    // the first run computes a real result and the server retries it
    // against the true node.
    let seed = null;
    try { seed = (await get(cardRef)).val(); } catch (_) { /* offline → cache path */ }
    let outcome = null;
    const res = await runTransaction(cardRef, (current) => {
      outcome = [];
      const base = current || seed;
      if (!base) return; // nothing owned → abort, node untouched
      const c = { ...base };
      const tiers = { ...(c.holoTiers || {}) };
      const sources = { ...(c.sources || {}) };
      const sourceTiers = {};
      for (const [t, m] of Object.entries(c.sourceTiers || {})) sourceTiers[t] = { ...(m || {}) };
      let holoCount = c.holoCount || 0;
      let count = c.count || 0;

      // Richest source first — which physical copy left is unknowable;
      // this is scrapCopies' labelled approximation, kept identical so
      // the two lanes never disagree about provenance.
      const decRichest = (map) => {
        let best = null;
        for (const k of Object.keys(map)) {
          if ((map[k] || 0) > 0 && (best === null || map[k] > map[best])) best = k;
        }
        if (best === null) return null;
        map[best] -= 1;
        if (map[best] <= 0) delete map[best];
        return best;
      };

      for (const r of reqs) {
        if (!r.tier) {
          const floor = floorOfOne ? 1 : 0;
          if (count - r.copies < floor) return; // cannot cover → abort whole card
          for (let i = 0; i < r.copies; i++) { count -= 1; decRichest(sources); }
        } else {
          if ((tiers[r.tier] || 0) < r.copies) return; // cannot cover → abort
          for (let i = 0; i < r.copies; i++) {
            tiers[r.tier] -= 1;
            if (tiers[r.tier] <= 0) delete tiers[r.tier];
            holoCount = Math.max(0, holoCount - 1);
            const m = sourceTiers[r.tier];
            if (m) {
              // Never hand out a Vault-born copy: take from the non-vault
              // sources first, and only fall back to vault if that is all
              // that is left (validateOffer should have refused that).
              const nonVault = Object.fromEntries(Object.entries(m).filter(([k]) => k !== "vault"));
              let src = decRichest(nonVault);
              if (src) {
                m[src] = (m[src] || 0) - 1;
                if (m[src] <= 0) delete m[src];
              } else {
                src = decRichest(m);
              }
              if (Object.keys(m).length === 0) delete sourceTiers[r.tier];
              if (src && (sources[src] || 0) > 0) {
                sources[src] -= 1;
                if (sources[src] <= 0) delete sources[src];
              }
            }
          }
        }
        outcome.push({ cardId, tier: r.tier, copies: r.copies });
      }

      let legacy = c.holoTier || null;
      if (legacy && !(tiers[legacy] > 0)) {
        legacy = null;
        for (const t of Object.keys(tiers)) { if (tiers[t] > 0) { legacy = t; break; } }
      }
      return {
        ...c,
        count: Math.max(0, count),   // rules: hasChildren(['count']) — always present, never < 0
        holoCount,
        holoTier: legacy,
        holoTiers: tiers,
        sources,
        sourceTiers,
      };
    });
    if (res.committed && Array.isArray(outcome) && outcome.length) removed.push(...outcome);
    else short.push(...reqs.map((r) => ({ cardId, tier: r.tier, copies: r.copies })));
  }
  return { ok: short.length === 0, removed, short };
};

// ════════════════════════════════════════════════════════════
// SHARED-NODE HELPERS
// ════════════════════════════════════════════════════════════
export const sideOf = (trade, uid) => {
  if (!trade || !uid) return null;
  if (trade.a?.uid === uid) return "a";
  if (trade.b?.uid === uid) return "b";
  return null;
};
export const otherSideOf = (side) => (side === "a" ? "b" : side === "b" ? "a" : null);

export const isExpired = (trade) =>
  !!trade && typeof trade.expiresAt === "number" && serverNow() > trade.expiresAt;

// A trade is still editable/cancellable while nobody has debited.
const _anyDebited = (t) => !!(t?.a?.debited || t?.b?.debited);

const _emptySide = (uid, name) => ({
  uid, name: clipName(name), offer: { coins: 0 },
  locked: null, confirmed: null, debited: null, credited: null,
});

// ── PROPOSE ──────────────────────────────────────────────────
// Write site: games/terror `".write": "auth != null"` — any authed
// player may create a trade node under the drawer. Returns the push id.
export const proposeTrade = async (meUid, meName, themUid, themName) => {
  meUid = _own(meUid, "proposeTrade");
  if (!meUid || !_looksLikeUid(themUid) || themUid === meUid) return null;
  const r = push(ref(db, tradesPath()));
  await set(r, {
    a: _emptySide(meUid, meName),
    b: _emptySide(themUid, themName),
    status: "draft",
    rev: 0,
    createdAt: serverTimestamp(),
    expiresAt: serverNow() + DRAFT_TTL_MS,
    cancelledBy: null,
  });
  return r.key;
};

export const getTrade = async (id) => {
  if (!id) return null;
  const s = await get(ref(db, tradePath(id)));
  return s.exists() ? s.val() : null;
};

// Read site: games/terror `".read": "auth != null"`.
export const listenToTrade = (id, cb) => {
  if (!id) return () => {};
  return onValue(ref(db, tradePath(id)), (s) => cb(s.exists() ? { id, ...s.val() } : null));
};

// The transaction wrapper every state transition goes through. `mut`
// returns the next node, or undefined to abort. Guards live in the
// callers; this only makes sure a null cache never produces a bogus
// write (same seed trick as the collection lane).
const _tx = async (id, mut) => {
  const r = ref(db, tradePath(id));
  let seed = null;
  try { seed = (await get(r)).val(); } catch (_) {}
  let result = null;
  const res = await runTransaction(r, (current) => {
    const base = current || seed;
    if (!base) return;
    const next = mut(base);
    result = next === undefined ? null : next;
    return next;
  });
  return { committed: res.committed && !!result, trade: res.committed ? res.snapshot.val() : null };
};

// ── EDIT MY OFFER ────────────────────────────────────────────
// Any edit bumps rev and DELETES both locks and both confirmations
// (the law: nobody is ever confirmed against an offer they have not
// seen). Refused once anyone has debited. Validates against the
// signed-in player's live collection + balance unless the caller hands
// them in (the UI holds the live profile, so it does).
export const updateOffer = async (id, uid, offer, { collection = null, coins = null } = {}) => {
  uid = _own(uid, "updateOffer");
  if (!uid || !id) return { ok: false, error: "Not signed in." };
  const clean = normalizeOffer(offer);
  if (collection === null) {
    try {
      const s = await get(ref(db, `profiles/${uid}`));
      collection = s.val()?.collection || {};
      if (coins === null) coins = s.val()?.chocolateCoins || 0;
    } catch (_) { collection = {}; }
  }
  const v = validateOffer(collection, clean, { coins });
  if (!v.ok) return { ok: false, error: v.errors[0], errors: v.errors };
  const res = await _tx(id, (t) => {
    const side = sideOf(t, uid);
    if (!side) return;
    if (!["draft", "locked"].includes(t.status)) return;
    if (_anyDebited(t)) return;
    return {
      ...t,
      [side]: { ...t[side], offer: clean, locked: null, confirmed: null },
      [otherSideOf(side)]: { ...t[otherSideOf(side)], locked: null, confirmed: null },
      status: "draft",
      rev: (t.rev || 0) + 1,
      expiresAt: serverNow() + DRAFT_TTL_MS,
    };
  });
  return res.committed ? { ok: true, trade: res.trade } : { ok: false, error: "That trade can no longer be edited." };
};

// ── LOCK ─────────────────────────────────────────────────────
// "I am happy with both halves as they stand at this rev." When both
// sides are locked at the same rev the trade is `locked` and CONFIRM
// opens. Locking a trade that has expired is refused.
export const lockOffer = async (id, uid) => {
  uid = _own(uid, "lockOffer");
  if (!uid || !id) return { ok: false, error: "Not signed in." };
  const res = await _tx(id, (t) => {
    const side = sideOf(t, uid);
    if (!side || !["draft", "locked"].includes(t.status) || isExpired(t)) return;
    if (offerIsEmpty(t.a?.offer) && offerIsEmpty(t.b?.offer)) return; // an empty trade is not a trade
    const now = serverNow();
    const mine = { ...t[side], locked: now };
    const both = !!t[otherSideOf(side)]?.locked;
    return { ...t, [side]: mine, status: both ? "locked" : "draft", expiresAt: now + DRAFT_TTL_MS };
  });
  return res.committed ? { ok: true, trade: res.trade } : { ok: false, error: "Could not lock — the trade has moved on." };
};

// ── UNLOCK — same law as an edit: bumps rev, clears every lock/confirm.
export const unlockOffer = async (id, uid) => {
  uid = _own(uid, "unlockOffer");
  if (!uid || !id) return { ok: false, error: "Not signed in." };
  const res = await _tx(id, (t) => {
    const side = sideOf(t, uid);
    if (!side || !["draft", "locked"].includes(t.status) || _anyDebited(t)) return;
    return {
      ...t,
      a: { ...t.a, locked: null, confirmed: null },
      b: { ...t.b, locked: null, confirmed: null },
      status: "draft", rev: (t.rev || 0) + 1, expiresAt: serverNow() + DRAFT_TTL_MS,
    };
  });
  return res.committed ? { ok: true } : { ok: false, error: "Could not unlock." };
};

// ── CANCEL — allowed until anyone has debited. After that the only
// exits are settlement or expiry + reclaim.
export const cancelTrade = async (id, uid) => {
  uid = _own(uid, "cancelTrade");
  if (!uid || !id) return { ok: false, error: "Not signed in." };
  const res = await _tx(id, (t) => {
    const side = sideOf(t, uid);
    if (!side || _anyDebited(t)) return;
    if (["settled", "cancelled", "expired"].includes(t.status)) return;
    return { ...t, status: "cancelled", cancelledBy: uid };
  });
  return res.committed ? { ok: true } : { ok: false, error: "Too late to cancel — a half has already moved." };
};

// ── EXPIRE — either party may stamp a dead draft as expired. Purely a
// tidy-up for the UI; expiry itself is a clock comparison, not a status.
export const expireTrade = async (id, uid) => {
  uid = _own(uid, "expireTrade");
  if (!uid || !id) return { ok: false };
  const res = await _tx(id, (t) => {
    if (!sideOf(t, uid) || !isExpired(t) || _anyDebited(t)) return;
    if (["settled", "cancelled", "expired"].includes(t.status)) return;
    return { ...t, status: "expired" };
  });
  return { ok: res.committed };
};

// ════════════════════════════════════════════════════════════
// THE TWO-PHASE COMMIT
// ════════════════════════════════════════════════════════════
// Build my sealed copy from the shared node — the ONLY moment the
// shared node is read as truth, and it is read at rev R, for a trade
// I have locked at rev R and can see the other side locked at rev R.
const _sealedFrom = (t, side, now) => {
  const other = otherSideOf(side);
  return {
    counterparty: t[other]?.uid || null,
    counterpartyName: clipName(t[other]?.name),
    give:    normalizeOffer(t[side]?.offer),
    receive: normalizeOffer(t[other]?.offer),
    rev: t.rev || 0,
    confirmedAt: now,
    expiresAt: now + CONFIRMED_TTL_MS,
    debitedAt: null, creditedAt: null, reclaimedAt: null,
  };
};

// ── CONFIRM ──────────────────────────────────────────────────
// (1) write my sealed copy  (2) mark confirmed on the shared node
// (3) if that made it `confirmed`, debit my give right away.
// The other side's client sees `confirmed` on its listener and runs
// settleDebit for itself (the UI does this — see useTradeSettlement).
export const confirmTrade = async (id, uid) => {
  uid = _own(uid, "confirmTrade");
  if (!uid || !id) return { ok: false, error: "Not signed in." };
  const t = await getTrade(id);
  const side = sideOf(t, uid);
  if (!t || !side) return { ok: false, error: "Trade not found." };
  if (t.status !== "locked" || !t.a?.locked || !t.b?.locked) return { ok: false, error: "Both sides must lock first." };
  if (isExpired(t)) return { ok: false, error: "This trade has expired." };

  // Re-validate my give against my LIVE collection: a scrap in another
  // tab since I locked must not let me confirm a give I cannot cover.
  let coins = 0, collection = {};
  try {
    const s = await get(ref(db, `profiles/${uid}`));
    collection = s.val()?.collection || {}; coins = s.val()?.chocolateCoins || 0;
  } catch (_) {}
  const v = validateOffer(collection, t[side].offer, { coins });
  if (!v.ok) return { ok: false, error: v.errors[0], errors: v.errors };

  // (1) THE SEAL. Refuse to overwrite a seal that has already debited —
  // that receipt is the only record of what left my collection.
  // Write site: profiles/$uid `".write": "auth != null && auth.uid === $uid"`;
  // `trades` is a new field under the profile root, which has no $extra
  // guard (only `collection/$cardId` and `friends/$friendUid` do).
  const sRef = ref(db, sealedPath(uid, id));
  const existing = (await get(sRef)).val();
  if (existing?.debitedAt) return { ok: false, error: "Already confirmed and debited." };
  const now = serverNow();
  await set(sRef, _sealedFrom(t, side, now));

  // (2) MARK CONFIRMED — guarded on rev + status so a peer edit between
  // my read and this write voids the confirmation instead of racing it.
  const res = await _tx(id, (cur) => {
    if (cur.status !== "locked" || (cur.rev || 0) !== (t.rev || 0)) return;
    if (!cur.a?.locked || !cur.b?.locked) return;
    const other = otherSideOf(side);
    const both = !!cur[other]?.confirmed;
    return {
      ...cur,
      [side]: { ...cur[side], confirmed: now },
      status: both ? "confirmed" : "locked",
      expiresAt: both ? now + CONFIRMED_TTL_MS : cur.expiresAt,
    };
  });
  if (!res.committed) {
    // The seal is stale — the trade moved. Clear it so it can never be
    // mistaken for a live receipt (it has no debitedAt, so nothing was lost).
    await set(sRef, null).catch(() => {});
    return { ok: false, error: "The offer changed under you — look again and re-lock." };
  }
  // (3) both confirmed → my debit, immediately.
  if (res.trade?.status === "confirmed") {
    const d = await settleDebit(uid, id);
    return { ok: true, trade: res.trade, debit: d };
  }
  return { ok: true, trade: res.trade };
};

// ── THE LEASE (2026-09-06) ──────────────────────────────────
// Every settlement step below is N separate writes with a cursor, and
// a cursor read once and written later is not a compare-and-set: two
// runners of the SAME player (phone + desktop, two tabs) both read 0
// and both mint every copy. So a step first claims a short lease on
// the sealed copy with a transaction; a second runner sees a live
// lease and steps back. A lease older than LEASE_MS is a dead tab and
// may be taken over — the cursor then resumes where the dead run left
// it, which is exactly what the cursor is for.
const LEASE_MS = 90 * 1000;
const _runId = Math.random().toString(36).slice(2, 10);
const _takeLease = async (sRef, field) => {
  // THE CACHE SEED, same dance as decrementCopies: the updater sees the
  // local cache first (null for a node nobody is listening to) and a
  // bare `return` on that null aborts WITHOUT asking the server.
  let seed = null;
  try { seed = (await get(sRef)).val(); } catch (_) {}
  const res = await runTransaction(sRef, (current) => {
    const cur = current || seed;
    if (!cur) return;                                   // no sealed copy → abort
    const l = cur[field];
    if (l && l.by !== _runId && (serverNow() - (l.at || 0)) < LEASE_MS) return;   // somebody else is running this step
    return { ...cur, [field]: { by: _runId, at: serverNow() } };
  });
  return !!res.committed;
};
const _dropLease = (sRef, field) => update(sRef, { [field]: null }).catch(() => {});

// ── DEBIT MY GIVE ────────────────────────────────────────────
// Idempotent on sealed.debitedAt. Coins first (spendCoins is the
// atomic check-and-debit — a false means "not enough" and the trade is
// aborted: cancelled on the shared node if nobody else has debited
// yet, so the counterparty is never left waiting on a half that will
// not come). Then cards, all-or-nothing per card. Then the stamps:
// sealed.debitedAt FIRST (my receipt), shared.{side}.debited second.
export const settleDebit = async (uid, id) => {
  uid = _own(uid, "settleDebit");
  if (!uid || !id) return { ok: false, error: "Not signed in." };
  const sRef = ref(db, sealedPath(uid, id));
  const sealed = (await get(sRef)).val();
  if (!sealed) return { ok: false, error: "No sealed copy." };
  if (sealed.debitedAt) return { ok: true, already: true };

  const t = await getTrade(id);
  const side = sideOf(t, uid);
  if (!t || !side) return { ok: false, error: "Trade not found." };
  if (!["confirmed", "debited", "settled"].includes(t.status)) return { ok: false, error: "Trade is not confirmed." };
  if ((t.rev || 0) !== (sealed.rev || 0) || !t.a?.confirmed || !t.b?.confirmed) {
    return { ok: false, error: "Sealed copy does not match the trade." };
  }
  // ⚠ NEVER DEBIT INTO A DEAD TRADE. If the window has closed, the other
  // side may already have RECLAIMED its half; a late debit here would let
  // their client see my `debited` and credit itself with copies I just
  // gave, while they also took theirs back — duplication. The same guard
  // covers a counterparty that has reclaimed inside the window.
  if (isExpired(t) || t[otherSideOf(side)]?.reclaimed) {
    return { ok: false, error: "This trade expired before your half was settled — nothing left your collection." };
  }

  const give = normalizeOffer(sealed.give);
  const abort = async (error) => {
    // Cancel while cancelling is still safe. If the other side already
    // debited, this becomes their reclaim after expiry — by design.
    await _tx(id, (cur) => (_anyDebited(cur) ? undefined : { ...cur, status: "cancelled", cancelledBy: uid }));
    return { ok: false, error };
  };

  if (!(await _takeLease(sRef, "debitLease"))) return { ok: false, error: "Settling in another window.", busy: true };
  try {
    // ⚠ RESUMABLE, STEP BY STEP — the same discipline as settleCredit.
    // The first cut spent the coins, removed the cards and only THEN
    // stamped debitedAt; a tab dying in between debited the player
    // twice on the next open. Coins are stamped the moment they leave
    // (`debitCoinsAt`); cards go one physical copy at a time behind
    // `debitCursor`, so a re-run starts at the copy after the last one
    // that actually left.
    const fresh = (await get(sRef)).val() || sealed;
    if (give.coins > 0 && !fresh.debitCoinsAt) {
      const paid = await spendCoins(uid, give.coins);
      if (!paid) { await _dropLease(sRef, "debitLease"); return abort("Not enough Chocolate Coins to cover your half."); }
      await update(sRef, { debitCoinsAt: serverNow() });
    }
    const flat = [];
    for (const it of offerToItems(give)) for (let i = 0; i < it.copies; i++) flat.push({ cardId: it.cardId, tier: it.tier, copies: 1 });
    let cursor = Number(fresh.debitCursor) || 0;
    for (let i = cursor; i < flat.length; i++) {
      const d = await decrementCopies(uid, [flat[i]], { floorOfOne: false });
      if (!d.ok) {
        // My collection changed since confirm. Put back what THIS
        // trade has taken so far — the coins and every copy behind the
        // cursor — and abort. Nothing of the counterparty's has moved.
        if (give.coins > 0) await awardCoins(uid, give.coins, `trade_refund_${id}`);
        for (let j = 0; j < i; j++) await addCardToCollection(uid, flat[j].cardId, !!flat[j].tier, flat[j].tier, TRADE_SOURCE);
        await update(sRef, { debitCoinsAt: null, debitCursor: null });
        await _dropLease(sRef, "debitLease");
        return abort("A card in your offer is no longer in your collection.");
      }
      cursor = i + 1;
      await update(sRef, { debitCursor: cursor });
    }
  } catch (e) {
    await _dropLease(sRef, "debitLease");
    throw e;
  }

  const now = serverNow();
  // Write site: profiles/$uid (owner) — my receipt first.
  await update(sRef, { debitedAt: now, debitLease: null });
  // Write site: games/terror (any authed) — then the flag the other side watches.
  const res = await _tx(id, (cur) => {
    const other = otherSideOf(side);
    const both = !!cur[other]?.debited;
    return { ...cur, [side]: { ...cur[side], debited: now }, status: both ? "debited" : cur.status };
  });
  return { ok: true, trade: res.trade };
};

// Has the counterparty debited? Shared node first, then THEIR sealed
// copy (profiles are world-readable: `".read": "auth != null"`), so a
// peer whose client died between their receipt and the shared flag is
// still seen.
export const counterpartyDebited = async (uid, id, sealed = null, trade = null) => {
  const t = trade || await getTrade(id);
  const side = sideOf(t, uid);
  // A half that was reclaimed is no longer on the table, whatever
  // `debited` still says — see reclaimTrade's shared-node stamp.
  if (t && side && t[otherSideOf(side)]?.reclaimed) return false;
  if (t && side && t[otherSideOf(side)]?.debited) return true;
  const them = sealed?.counterparty || (t && side ? t[otherSideOf(side)]?.uid : null);
  if (!_looksLikeUid(them)) return false;
  try {
    const s = await get(ref(db, `${sealedPath(them, id)}/debitedAt`));
    return !!s.val();
  } catch (_) { return false; }
};

// ── CREDIT MY RECEIVE ────────────────────────────────────────
// Gated on: my debit done, my receipt not reclaimed, not already
// credited, and the other side's debit VISIBLE. Every copy goes through
// addCardToCollection with source "trade"; coins through awardCoins
// (one transaction; TRADE_COIN_CAP keeps it under the rules' 200000 clamp).
// Idempotent on sealed.creditedAt. reclaimedAt aborts — a reclaimed half
// must never also collect.
export const settleCredit = async (uid, id) => {
  uid = _own(uid, "settleCredit");
  if (!uid || !id) return { ok: false, error: "Not signed in." };
  const sRef = ref(db, sealedPath(uid, id));
  const sealed = (await get(sRef)).val();
  if (!sealed) return { ok: false, error: "No sealed copy." };
  if (sealed.creditedAt) return { ok: true, already: true };
  if (sealed.reclaimedAt) return { ok: false, error: "This half was reclaimed." };
  if (!sealed.debitedAt) return { ok: false, error: "Your half has not been debited yet." };

  const t = await getTrade(id);
  if (!(await counterpartyDebited(uid, id, sealed, t))) return { ok: false, error: "Waiting on the other side.", waiting: true };

  // ⚠ RESUMABLE, COPY BY COPY. The credit is N separate collection
  // transactions plus one coin delta, and a tab that dies after copy 3
  // of 7 used to re-run all seven on the next open — four copies minted
  // twice. `creditCursor` on the sealed copy is stamped after every
  // landed copy, and the coin grant is stamped before creditedAt, so a
  // re-run resumes exactly where the last one stopped. Over-crediting
  // is the one failure this lane must never have: it is duplication.
  if (!(await _takeLease(sRef, "creditLease"))) return { ok: false, error: "Collecting in another window.", busy: true };
  try {
    const fresh = (await get(sRef)).val() || sealed;   // the cursor, re-read under the lease
    const recv = normalizeOffer(fresh.receive);
    const flat = [];
    for (const it of offerToItems(recv)) for (let i = 0; i < it.copies; i++) flat.push(it);
    let cursor = Number(fresh.creditCursor) || 0;
    for (let i = cursor; i < flat.length; i++) {
      const it = flat[i];
      await addCardToCollection(uid, it.cardId, !!it.tier, it.tier, TRADE_SOURCE);
      cursor = i + 1;
      await update(sRef, { creditCursor: cursor });
    }
    if (recv.coins > 0 && !fresh.coinsCredited) {
      await awardCoins(uid, Math.min(recv.coins, TRADE_COIN_CAP), `trade_${id}`);
      await update(sRef, { coinsCredited: true });
    }
  } catch (e) {
    await _dropLease(sRef, "creditLease");
    throw e;
  }

  const now = serverNow();
  await update(sRef, { creditedAt: now, creditLease: null });
  const side = sideOf(t, uid);
  if (side) {
    await _tx(id, (cur) => {
      const both = !!cur[otherSideOf(side)]?.credited;
      return { ...cur, [side]: { ...cur[side], credited: now }, status: both ? "settled" : cur.status };
    });
  }
  return { ok: true };
};

// ── RECLAIM ──────────────────────────────────────────────────
// After expiry only, and only for a half that debited and never
// credited. One more guard the brief did not spell out but the math
// demands: if the OTHER side has debited, the right move is Collect,
// not Reclaim — reclaiming while they debited would hand my give back
// to me while their client credits itself with the same copies. So
// reclaim redirects to settleCredit in that case.
export const reclaimTrade = async (uid, id) => {
  uid = _own(uid, "reclaimTrade");
  if (!uid || !id) return { ok: false, error: "Not signed in." };
  const sRef = ref(db, sealedPath(uid, id));
  const sealed = (await get(sRef)).val();
  if (!sealed) return { ok: false, error: "No sealed copy." };
  if (sealed.creditedAt) return { ok: false, error: "Already collected." };
  if (sealed.reclaimedAt) return { ok: true, already: true };
  if (!sealed.debitedAt) return { ok: false, error: "Nothing left your collection." };
  const t = await getTrade(id);
  const expiresAt = Number(t?.expiresAt || sealed.expiresAt || 0);
  if (serverNow() <= expiresAt) return { ok: false, error: "Not expired yet." };
  if (await counterpartyDebited(uid, id, sealed, t)) {
    const c = await settleCredit(uid, id);
    return c.ok ? { ok: true, collected: true } : c;
  }
  // STAMP THE SHARED NODE FIRST. From this write on the counterparty's
  // settleDebit refuses and counterpartyDebited answers false for my
  // side, so nobody can credit themselves with what I am taking back.
  const now = serverNow();
  const side = sideOf(t, uid);
  if (side) {
    await _tx(id, (cur) => ({ ...cur, [side]: { ...cur[side], reclaimed: now }, status: "expired" }));
  }
  // Resumable, copy by copy, under a lease — same reasons as settleCredit.
  if (!(await _takeLease(sRef, "reclaimLease"))) return { ok: false, error: "Reclaiming in another window.", busy: true };
  try {
    const fresh = (await get(sRef)).val() || sealed;
    const give = normalizeOffer(fresh.give);
    const flat = [];
    for (const it of offerToItems(give)) for (let i = 0; i < it.copies; i++) flat.push(it);
    let cursor = Number(fresh.reclaimCursor) || 0;
    for (let i = cursor; i < flat.length; i++) {
      const it = flat[i];
      await addCardToCollection(uid, it.cardId, !!it.tier, it.tier, TRADE_SOURCE);
      cursor = i + 1;
      await update(sRef, { reclaimCursor: cursor });
    }
    if (give.coins > 0 && !fresh.coinsReclaimed) {
      await awardCoins(uid, give.coins, `trade_reclaim_${id}`);
      await update(sRef, { coinsReclaimed: true });
    }
  } catch (e) {
    await _dropLease(sRef, "reclaimLease");
    throw e;
  }
  await update(sRef, { reclaimedAt: serverNow(), reclaimLease: null });
  return { ok: true, reclaimed: true };
};

// ════════════════════════════════════════════════════════════
// HOOKS
// ════════════════════════════════════════════════════════════
// Sealed copies with a debit and no credit/reclaim — the halves that
// still owe the player something. Each row is { id, ...sealed }.
export function usePendingTrades(uid) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!_looksLikeUid(uid)) { setRows([]); return; }
    return onValue(ref(db, sealedRoot(uid)), (s) => {
      const v = s.val() || {};
      const out = [];
      for (const [id, sealed] of Object.entries(v)) {
        if (!sealed || sealed.creditedAt || sealed.reclaimedAt) continue;
        // Two shapes live here: a half that has DEBITED and waits to
        // collect/reclaim, and a half that CONFIRMED but never debited
        // (the post was closed before the other side confirmed) — that
        // one was invisible, and the counterparty's give sat frozen for
        // the whole 24h window with no way for this player to find it.
        if (sealed.debitedAt) out.push({ id, ...sealed, stage: "debited" });
        else if (sealed.confirmedAt) out.push({ id, ...sealed, stage: "confirmed" });
      }
      out.sort((x, y) => (y.debitedAt || y.confirmedAt || 0) - (x.debitedAt || x.confirmedAt || 0));
      setRows(out);
    });
  }, [uid]);
  return rows;
}

// Live shared node. null = not loaded / gone.
export function useTrade(id) {
  const [trade, setTrade] = useState(null);
  useEffect(() => { setTrade(null); return listenToTrade(id, setTrade); }, [id]);
  return trade;
}

// My sealed copy for one trade, live.
export function useSealed(uid, id) {
  const [sealed, setSealed] = useState(null);
  useEffect(() => {
    if (!_looksLikeUid(uid) || !id) { setSealed(null); return; }
    return onValue(ref(db, sealedPath(uid, id)), (s) => setSealed(s.exists() ? s.val() : null));
  }, [uid, id]);
  return sealed;
}

// ════════════════════════════════════════════════════════════
// THE INVITE CHANNEL
// ════════════════════════════════════════════════════════════
// A trade rides the EXISTING invite (invites/$to/$from), because that
// node is a key-name whitelist (`"$extra": { ".validate": false }`)
// and a new field would be refused. The id travels in `note` as
// "trade:<id>" (≤ 80 chars) and on `url` as `?trade=<id>` (validated
// by prefix only). Push keys are 20 chars of [-_A-Za-z0-9].
export const tradeIdOf = (inv) => {
  if (!inv) return null;
  if (inv.note) {
    const m = /^trade:([-_A-Za-z0-9]{1,40})/.exec(String(inv.note));
    if (m) return m[1];
  }
  if (inv.url) {
    const m = /[?&]trade=([-_A-Za-z0-9]{1,40})/.exec(String(inv.url));
    if (m) return m[1];
  }
  return null;
};

// What the caller hands to F.invite(uid, ...) — one place for the shape.
export const tradeInviteOpts = (id, baseUrl = (IE_GAME_URLS && IE_GAME_URLS.terror) || "https://terror-card-game.pages.dev") => ({
  gameId: "terror",
  note: `trade:${id}`,
  label: "Trade offer",
  url: `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}trade=${id}`,
});

// Same pattern as ieSocial's setMatchInviteHandler: the Trade Post
// registers one function so an incoming trade invite opens the post
// in place instead of navigating.
let _tradeOpener = null;
export function setTradeInviteHandler(fn) { _tradeOpener = typeof fn === "function" ? fn : null; }
export function tradeInviteHandler() { return _tradeOpener; }

// Convenience for a toast / URL reader: `?trade=` off the current page.
export const tradeIdFromLocation = () => {
  try { return tradeIdOf({ url: window.location.href }); } catch { return null; }
};
